/* eslint-disable @typescript-eslint/no-explicit-any */
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_LIMIT_REQUEST } from '@/Constants';
import type { IMember } from '@/Interfaces/Data';

import Requests from '@/Services/Requests';
import {
  getMemberSuccess,
  membersFailure,
  membersRequest,
  membersSuccess,
  selectMembers,
  setMemberIsActiveRequest,
  setMembersPage,
  setMembersTotalCount,
  setRegistrationMemberRequest,
  getMember as sliceGetMember,
} from '.';
import type { MembersRequest } from './types';

export function* setMembers(action: PayloadAction<MembersRequest>): Generator {
  try {
    const { page: pageAction, name, cityId, isActive } = action.payload;

    const page = pageAction || 1;
    const limit = DEFAULT_LIMIT_REQUEST;

    const offset = (page - 1) * limit;

    const response: any = yield call(
      Requests.members.getMembers,
      DEFAULT_LIMIT_REQUEST,
      offset,
      name,
      cityId,
      isActive,
    );

    if (response.r) {
      yield put(membersSuccess(response.data));
      yield put(setMembersTotalCount(response.totalCount));
      yield put(setMembersPage(page));
    } else {
      yield put(membersFailure([...response.errors]));
    }
  } catch (err: any) {
    const errorMessage = err.response?.data.error;
    yield put(membersFailure([errorMessage]));
  }
}

export function* getMember(action: {
  type: string;
  payload: { id: string };
}): Generator {
  try {
    const response: any = yield call(
      Requests.members.getMemberById,
      action.payload.id,
    );

    if (response.r) {
      yield put(getMemberSuccess(response.data));
    } else {
      yield put(membersFailure([...response.errors]));
    }
  } catch (err: any) {
    const errorMessage = err.response?.data.error;
    yield put(membersFailure([errorMessage]));
  }
}

export function* setIsActive(action: any): Generator {
  try {
    const members: any = yield select(selectMembers);

    const { memberId, isActive } = action.payload;

    yield call(Requests.members.setActive, memberId, isActive);
    const payload = members.map((e: IMember) => ({
      ...e,
      isAtivo: e._id === memberId ? isActive : e.isAtivo,
    }));
    yield put(membersSuccess(payload));
  } catch (err: any) {
    const errorMessage = err.response?.data.error;
    yield put(membersFailure([errorMessage]));
  }
}

export function* setRegistrationMember(action: any): Generator {
  try {
    if (action.payload.id) {
      yield call(
        Requests.members.putRegistrationMember,
        action.payload.id,
        action.payload.payload,
      );
    } else {
      yield call(
        Requests.members.postRegistrationMember,
        action.payload.payload,
      );
    }
    yield put(membersSuccess([]));
  } catch (err: any) {
    const errorMessage = err.response?.data.error;
    yield put(membersFailure([errorMessage]));
  }
}

export default all([
  takeLatest(membersRequest.type, setMembers),
  takeLatest(sliceGetMember.type, getMember),
  takeLatest(setMemberIsActiveRequest.type, setIsActive),
  takeLatest(setRegistrationMemberRequest.type, setRegistrationMember),
]);
